import { readRoomMessage, type PartySnapshot } from './protocol';
import { MAX_PLAYERS } from '../core/players';

export type PartyCoordinator = {
  readonly allReady: boolean;
  readonly allVoted: boolean;
  readonly canStart: boolean;
  snapshot(): PartySnapshot;
  /** Membership changes invalidate every ballot, including changes during a match. */
  syncRoster(ids: Iterable<number>): boolean;
  /** Call after changing the rules that the next match will use. */
  invalidateRules(): void;
  setArenaChoices(choices: string[]): boolean;
  setReady(peerId: number, ready: boolean, epoch?: number): boolean;
  setVote(peerId: number, vote: boolean, matchId: string, epoch?: number): boolean;
  setArenaVote(peerId: number, levelId: string, matchId: string, epoch?: number): boolean;
  /** Only validated guest ballot messages are accepted; `from` comes from the relay envelope. */
  receive(from: number, raw: unknown): boolean;
  /** Call immediately before sending a new match; a stale async start cannot consume new ballots. */
  startMatch(matchId: string, expectedEpoch?: number): boolean;
  finishMatch(matchId: string, arenaChoices?: string[]): boolean;
  /** A deterministic round recovery keeps the room and phase, but retires the old match id. */
  recoverMatch(previousMatchId: string, nextMatchId: string): boolean;
  returnToLobby(): void;
};

function validId(id: number): boolean {
  return Number.isSafeInteger(id) && id > 0;
}

function validMatchId(id: string): boolean {
  return /^[a-zA-Z0-9-]{1,64}$/.test(id);
}

/** Pure host authority for a room. No timers, sockets or UI are owned by this coordinator. */
export function createParty(hostId: number, ids: Iterable<number> = [hostId]): PartyCoordinator {
  const initial = [...ids];
  if (
    !validId(hostId) ||
    !initial.includes(hostId) ||
    initial.length > MAX_PLAYERS ||
    initial.some((id) => !validId(id)) ||
    new Set(initial).size !== initial.length
  ) {
    throw new Error(`A party needs its host and at most ${MAX_PLAYERS} unique members.`);
  }
  const state: PartySnapshot = {
    t: 'party',
    epoch: 1,
    revision: 1,
    phase: 'lobby',
    matchId: null,
    members: initial,
    ready: [],
    votes: [],
  };
  const everyone = (ballots: number[]) => state.members.every((id) => ballots.includes(id));
  function newContext() {
    state.epoch += 1;
    state.revision += 1;
    state.ready = [];
    state.votes = [];
    state.arenaChoices = [];
    state.arenaVotes = [];
  }
  function ballot(list: 'ready' | 'votes', id: number, enabled: boolean, epoch: number): boolean {
    if (epoch !== state.epoch || !state.members.includes(id) || typeof enabled !== 'boolean')
      return false;
    if (state[list].includes(id) === enabled) return false;
    state[list] = enabled ? [...state[list], id] : state[list].filter((member) => member !== id);
    state.revision += 1;
    return true;
  }
  const api: PartyCoordinator = {
    get allReady() {
      return state.phase === 'lobby' && everyone(state.ready);
    },
    get allVoted() {
      return state.phase === 'rematch' && everyone(state.votes);
    },
    get canStart() {
      return api.allReady || api.allVoted;
    },
    snapshot: () => ({
      ...state,
      members: [...state.members],
      ready: [...state.ready],
      votes: [...state.votes],
      arenaChoices: [...(state.arenaChoices ?? [])],
      arenaVotes: (state.arenaVotes ?? []).map((vote) => ({ ...vote })),
    }),
    syncRoster(ids) {
      const next = [...ids];
      if (
        !next.includes(hostId) ||
        next.length > MAX_PLAYERS ||
        next.some((id) => !validId(id)) ||
        new Set(next).size !== next.length
      )
        return false;
      if (next.length === state.members.length && next.every((id) => state.members.includes(id)))
        return false;
      state.members = next;
      newContext();
      return true;
    },
    invalidateRules: newContext,
    setArenaChoices(choices) {
      if (state.phase !== 'rematch' || choices.length > 3 || new Set(choices).size !== choices.length || choices.some((id) => !id || id.length > 128)) return false;
      if (JSON.stringify(choices) === JSON.stringify(state.arenaChoices ?? [])) return false;
      newContext();
      state.arenaChoices = [...choices];
      return true;
    },
    setReady(peerId, ready, epoch = state.epoch) {
      return state.phase === 'lobby' && ballot('ready', peerId, ready, epoch);
    },
    setVote(peerId, vote, matchId, epoch = state.epoch) {
      return (
        state.phase === 'rematch' &&
        matchId === state.matchId &&
        ballot('votes', peerId, vote, epoch)
      );
    },
    receive(from, raw) {
      // A guest never supplies another player's id, and the host uses the local setters.
      if (from === hostId || !state.members.includes(from)) return false;
      const msg = readRoomMessage(raw, 'client');
      if (msg?.t === 'ready') return api.setReady(from, msg.ready, msg.epoch);
      if (msg?.t === 'rematch') return api.setVote(from, msg.vote, msg.matchId, msg.epoch);
      if (msg?.t === 'arena-vote') return api.setArenaVote(from, msg.levelId, msg.matchId, msg.epoch);
      return false;
    },
    setArenaVote(peerId, levelId, matchId, epoch = state.epoch) {
      if (epoch !== state.epoch || state.phase !== 'rematch' || matchId !== state.matchId || !state.members.includes(peerId) || !state.arenaChoices?.includes(levelId)) return false;
      if (state.arenaVotes?.some((vote) => vote.peer === peerId && vote.levelId === levelId)) return false;
      state.arenaVotes = [...(state.arenaVotes ?? []).filter((vote) => vote.peer !== peerId), { peer: peerId, levelId }];
      state.votes = state.votes.filter((id) => id !== peerId);
      state.revision++;
      return true;
    },
    startMatch(matchId, expectedEpoch = state.epoch) {
      if (!validMatchId(matchId) || expectedEpoch !== state.epoch || !api.canStart) return false;
      newContext();
      state.phase = 'playing';
      state.matchId = matchId;
      return true;
    },
    finishMatch(matchId, arenaChoices = []) {
      if (state.phase !== 'playing' || state.matchId !== matchId) return false;
      if (arenaChoices.length > 3 || new Set(arenaChoices).size !== arenaChoices.length || arenaChoices.some((id) => !id || id.length > 128)) return false;
      newContext();
      state.phase = 'rematch';
      state.arenaChoices = [...arenaChoices];
      return true;
    },
    recoverMatch(previousMatchId, nextMatchId) {
      if (
        state.phase === 'lobby' ||
        state.matchId !== previousMatchId ||
        !validMatchId(nextMatchId) ||
        nextMatchId === previousMatchId
      )
        return false;
      newContext();
      state.matchId = nextMatchId;
      return true;
    },
    returnToLobby() {
      newContext();
      state.phase = 'lobby';
      state.matchId = null;
    },
  };
  return api;
}

/** Clients apply only a newer, internally consistent snapshot from the room's current host. */
export function acceptPartySnapshot(
  current: PartySnapshot | null,
  raw: unknown,
  from: number,
  hostId: number,
): PartySnapshot | null {
  if (from !== hostId) return null;
  const msg = readRoomMessage(raw, 'host');
  if (msg?.t !== 'party' || !msg.members.includes(hostId)) return null;
  if (current && (msg.revision <= current.revision || msg.epoch < current.epoch)) return null;
  return msg;
}
